// Modal de cadastro/edição de um item da loja.
//
// O que sobrou aqui: o formulário do item (tipo, nome, descrição), a carga do
// que veio do banco e o rodapé que salva. Tudo o mais mora em ./item:
//   catalogoDeItens / catalogoDeComponentes  as listas
//   useOpcoesDeVenda                         estado das telas de PDV
//   ConstrutorVisualDoPdv                    o editor visual (arrasta e solta)
//   EditorDoComponente                       a sobreposição de propriedades
//   CamposDeItemSimples                      os campos de item que não é PDV
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/ItemModal";
import { ItemModalProps, RelatorioDasTelas, TelaDePdv } from "./item/tipos";
import { ITEM_TYPES, ROTULOS_DE_CATEGORIA } from "./item/catalogoDeItens";
import { useOpcoesDeVenda } from "./item/useOpcoesDeVenda";
import { ConstrutorVisualDoPdv } from "./item/ConstrutorVisualDoPdv";
import { EditorDoComponente } from "./item/EditorDoComponente";
import { CamposDeItemSimples } from "./item/CamposDeItemSimples";
import { lerTelaDePdv } from "./item/lerTelaDePdv";
import itemsService from "../../../services/item.service";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

const ItemModal: React.FC<ItemModalProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  setTempFormData,
  tempFormData,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-itemmodal",
  });

  /*
   * O construtor em tela cheia. Ver o comentário de `VisualBuilderWrapper`:
   * três colunas dentro de um modal deixavam a PRÉVIA — que é o motivo do
   * construtor existir — com a menor parte da caixa.
   */
  const [construtorEmTelaCheia, setConstrutorEmTelaCheia] = useState(false);

  const [localFormData, setLocalFormData] = useState({
    itemType: 1,
    itemName: "",
    itemDescription: "",
    viewType: "paisagem",
    backgroundColor: "#ffffff",
    textColor: "#000000",
    buttonsColor: "#4261FF",
    logo: "default",
    logoWidth: 80,
    logoHeight: 80,
    buttonsWidth: 60,
    buttonsHeight: 60,
    homeTitle: "Bem-vindo ao PDV",
    defaultPrinter: "",
    openPeriod: false,
    askCashValue: false,
    initialBalance: "0.00",
    tableNumber: "",
    capacity: 1,
    status: "active",
    ipAddress: "",
    port: "9100",
    printerType: "network",
    connectionType: "usb",
    deviceModel: "",
    macAddress: "",
    kitchenType: "preparation",
    paymentTerminalType: "pinpad",
    tabletOrientation: "landscape",
    displaySize: "medium",
    saleOptions: [],
  });

  // As duas velocidades: a alteração vale já, ou só quando o caixa abrir.
  // Quem escolhe é quem está salvando.
  const [aplicarEm, setAplicarEm] = useState<"agora" | "proximoPeriodo">(
    "agora"
  );

  // O que está na fila de configuração deste PDV. O PATCH volta 2xx assim que o
  // evento é enfileirado — quem decide se o PDV aceita é o worker do OPS,
  // segundos depois. Sem ler a fila, esta tela não teria como saber que a
  // configuração do usuário virou conflito, e ficaria afirmando um sucesso que
  // não existe. Mesmo padrão de MeiosPagamento.tsx.
  const [fila, setFila] = useState<any>(null);
  // O que o último Salvar produziu, dito na cara do usuário.
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const carregarFila = useCallback(async () => {
    const itemUuid = selectedObject?.itemUuid;
    if (!itemUuid) return;
    try {
      const situacao = await itemsService.getFilaPorItem(itemUuid);
      if (montado.current) setFila(situacao);
    } catch {
      /* o editor funciona sem a fila; não vale derrubar o modal por isso */
    }
  }, [selectedObject]);

  useEffect(() => {
    carregarFila();
  }, [carregarFila]);

  const {
    saleOptions,
    setSaleOptions,
    selectedSaleOption,
    setSelectedSaleOption,
    editingComponent,
    setEditingComponent,
    addComponentToSaleOption,
    removeComponentFromSaleOption,
    updateComponentInSaleOption,
    handleComponentPropChange,
  } = useOpcoesDeVenda();

  const isPDV = () => {
    const itemType = ITEM_TYPES.find((t) => t.id === localFormData.itemType);
    return itemType?.category === "pdv";
  };

  useEffect(() => {
    if (selectedObject) {
      const itemTypeInfo = ITEM_TYPES.find(
        (t) => t.id === selectedObject.itemType
      );
      const initialData = {
        itemType: selectedObject.itemType || 1,
        itemName: selectedObject.itemName || "",
        itemDescription: selectedObject.itemDescription || "",
        viewType:
          tempFormData.viewType || itemTypeInfo?.orientation || "paisagem",
        backgroundColor: tempFormData.backgroundColor || "#ffffff",
        textColor: tempFormData.textColor || "#000000",
        buttonsColor: tempFormData.buttonsColor || "#4261FF",
        logo: tempFormData.logo || "default",
        logoWidth: tempFormData.logoWidth || 80,
        logoHeight: tempFormData.logoHeight || 80,
        buttonsWidth: tempFormData.buttonsWidth || 60,
        buttonsHeight: tempFormData.buttonsHeight || 60,
        homeTitle: tempFormData.homeTitle || "Bem-vindo ao PDV",
        defaultPrinter: tempFormData.defaultPrinter || "",
        openPeriod: tempFormData.openPeriod || false,
        askCashValue: tempFormData.askCashValue || false,
        initialBalance: tempFormData.initialBalance || "0.00",
        tableNumber: tempFormData.tableNumber || "",
        capacity: tempFormData.capacity || 1,
        status: tempFormData.status || "active",
        ipAddress: tempFormData.ipAddress || "",
        port: tempFormData.port || "9100",
        printerType: tempFormData.printerType || "network",
        connectionType: tempFormData.connectionType || "usb",
        deviceModel: tempFormData.deviceModel || "",
        macAddress: tempFormData.macAddress || "",
        kitchenType: tempFormData.kitchenType || "preparation",
        paymentTerminalType: tempFormData.paymentTerminalType || "pinpad",
        tabletOrientation: tempFormData.tabletOrientation || "landscape",
        displaySize: tempFormData.displaySize || "medium",
        saleOptions: [],
      };

      setLocalFormData(initialData);
      setTempFormData(initialData);

      if (selectedObject.itemInfo && isPDV()) {
        const saleOptionsData = selectedObject.itemInfo
          .filter((info: any) => info.itemInfoType === 2)
          .map((info: any) => lerTelaDePdv(info));
        setSaleOptions(saleOptionsData);
        if (saleOptionsData.length > 0) {
          setSelectedSaleOption(saleOptionsData[0]);
        }
      }
    }
  }, [selectedObject]);

  const handleLocalChange = (field: string, value: any) => {
    const newData = {
      ...localFormData,
      [field]: value,
    };

    if (field === "itemType") {
      const itemTypeInfo = ITEM_TYPES.find((t) => t.id === value);
      if (itemTypeInfo?.orientation) {
        newData.viewType = itemTypeInfo.orientation;
      }
    }

    setLocalFormData(newData);
    setTempFormData(newData);
  };

  /**
   * Decide, tela por tela, o que pode ir para o servidor.
   *
   * Três recusas, todas antes de qualquer requisição sair:
   *   JSON quebrado   -> pular (gravar por cima seria trocar o valor bom por {})
   *   nada mudou      -> pular (o backend não tem curto-circuito por hash: uma
   *                      abertura seguida de Salvar já reescreveria a linha e
   *                      criaria evento na fila sem nenhuma edição)
   *   formato antigo  -> BLOQUEAR (o editor abriu essa tela vazia; salvar
   *                      gravaria o vazio por cima do cardápio)
   */
  const separarTelas = (): {
    alteradas: any[];
    relatorio: RelatorioDasTelas;
  } => {
    const alteradas: any[] = [];
    const bloqueadasFormatoAntigo: string[] = [];
    const invalidas: string[] = [];

    for (const option of saleOptions as TelaDePdv[]) {
      const nome = option.itemInfoName || option.itemInfoUuid || "tela sem nome";

      if (!option.parseOk) {
        invalidas.push(nome);
        continue;
      }

      const valorAgora = JSON.stringify(option.parsedValue);
      if (valorAgora === option.valorOriginal) continue;

      // O julgamento é sobre o valor que VEIO DO BANCO, não sobre o que está na
      // tela agora: arrastar um componente para uma tela do formato antigo cria
      // um `saleOptionComponents` do lado do `components` velho, e o teste
      // deixaria de reconhecer o formato justamente no caso perigoso.
      if (option.formatoAntigo) {
        bloqueadasFormatoAntigo.push(nome);
        continue;
      }

      alteradas.push({ ...option, itemInfoValue: valorAgora });
    }

    return {
      alteradas,
      relatorio: {
        total: saleOptions.length,
        bloqueadasFormatoAntigo,
        invalidas,
      },
    };
  };

  const handleSubmit = async () => {
    if (!localFormData.itemName) {
      alert("Por favor, preencha o nome do item.");
      return;
    }

    const { alteradas, relatorio } = isPDV()
      ? separarTelas()
      : {
          alteradas: [],
          relatorio: {
            total: 0,
            bloqueadasFormatoAntigo: [],
            invalidas: [],
          } as RelatorioDasTelas,
        };

    const saveData = {
      ...localFormData,
      // Velocidade escolhida no rodapé: "agora" ou "proximoPeriodo".
      aplicarEm,
      itemUuid: selectedObject?.itemUuid,
      saleOptions: alteradas,
      telas: relatorio,
    };

    const resultado: any = await handleSave("item", saveData);
    // Quem pinta o resultado é o modal, não a store: o `MessageBox` que lê
    // `messageZustand` só existe em LoginPage/RegisterPage — dentro da
    // Configuração aquela mensagem nunca apareceu para ninguém.
    if (resultado && montado.current) setAviso(resultado);
    // Se deu certo o modal já fechou e isto não chega a pintar nada. Se o PDV
    // recusou, o modal continua aberto — e é aqui que o bloco "Situação da
    // fila" passa a mostrar o "Recusado: ...".
    await carregarFila();
  };

  const renderItemSpecificFields = () => {
    if (isPDV()) {
      const construtor = (
        <DndProvider backend={HTML5Backend}>
          <AlfredoStyles.VisualBuilderWrapper $cheia={construtorEmTelaCheia}>
            {construtorEmTelaCheia && (
              <AlfredoStyles.BarraDaTelaCheia>
                <span>
                  Construtor do PDV
                  {selectedSaleOption?.itemInfoName
                    ? ` — ${selectedSaleOption.itemInfoName}`
                    : ""}
                </span>
                <AlfredoStyles.BotaoDaTelaCheia
                  type="button"
                  onClick={() => setConstrutorEmTelaCheia(false)}
                >
                  <span className="material-icons">fullscreen_exit</span>
                  Voltar ao formulário
                </AlfredoStyles.BotaoDaTelaCheia>
              </AlfredoStyles.BarraDaTelaCheia>
            )}
            <ConstrutorVisualDoPdv
              selectedSaleOption={selectedSaleOption}
              localFormData={localFormData}
              onUpdateComponent={updateComponentInSaleOption}
              onAddComponent={addComponentToSaleOption}
              editingComponent={editingComponent}
              onSelectComponent={setEditingComponent}
            />
          </AlfredoStyles.VisualBuilderWrapper>
        </DndProvider>
      );

      /*
       * Em tela cheia o construtor sai do fluxo do modal e vai para o `body`
       * por portal. Sem isso ele continuaria dentro de um ancestral com
       * `overflow: hidden` e `transform` — e `position: fixed` dentro de um
       * elemento transformado passa a se posicionar em relação a ELE, não à
       * janela, que é o defeito clássico e silencioso desse padrão.
       */
      if (construtorEmTelaCheia) {
        return ReactDOM.createPortal(construtor, document.body);
      }

      return (
        <AlfredoStyles.ItemBottom style={{ height: "calc(var(--u) * 600)" }}>
          <AlfredoStyles.BotaoDaTelaCheia
            type="button"
            style={{ position: "absolute", right: 0, top: 0, zIndex: 5 }}
            onClick={() => setConstrutorEmTelaCheia(true)}
            title="O construtor tem três colunas; em tela cheia a prévia fica utilizável"
          >
            <span className="material-icons">fullscreen</span>
            Tela cheia
          </AlfredoStyles.BotaoDaTelaCheia>
          {construtor}
        </AlfredoStyles.ItemBottom>
      );
    }
    return (
      <CamposDeItemSimples
        localFormData={localFormData}
        handleLocalChange={handleLocalChange}
      />
    );
  };

  const groupedItemTypes = ITEM_TYPES.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, typeof ITEM_TYPES>);

  const currentItemType = ITEM_TYPES.find(
    (t) => t.id === localFormData.itemType
  );

  // Nome e descrição ficaram travados enquanto `PATCH /item/:uuid` respondia
  // 500 ("Unknown route: /item/:uuid", rota fora do mapa do `hasPermission`):
  // um campo que aceita digitação e descarta em silêncio é pior do que um campo
  // desabilitado. A rota foi consertada no backend — entrou no `routeMappings`
  // e o handler grava nome/descrição/tipo com autoria e data —, então o travão
  // saiu junto com o aviso que explicava a trava. Quem grava é o `case "item"`
  // da Configuração, com o PATCH do item antes dos PATCH de `itemInfo`.

  const temFila =
    (fila?.pendentesAgora?.length ?? 0) > 0 ||
    (fila?.pendentesProximoPeriodo?.length ?? 0) > 0 ||
    (fila?.conflitos?.length ?? 0) > 0;

  return ReactDOM.createPortal(
    <>
      <AlfredoStyles.ModalOverlay>
        <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo}
          large
          style={{ width: "95%", maxWidth: "calc(var(--u) * 1400)", height: "90vh" }}
        >
          <AlfredoStyles.ModalHeader>
            <AlfredoStyles.ModalTitle id="titulo-itemmodal">
              <span className="material-icons">devices</span>
              {selectedObject
                ? `Editar Item - ${selectedObject.itemName}`
                : "Cadastrar Novo Item na Loja"}
              {currentItemType && (
                <AlfredoStyles.CategoryBadge
                  category={currentItemType.category}
                >
                  {ROTULOS_DE_CATEGORIA[currentItemType.category]}
                </AlfredoStyles.CategoryBadge>
              )}
            </AlfredoStyles.ModalTitle>
            <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
              <span className="material-icons">close</span>
            </AlfredoStyles.ModalCloseButton>
          </AlfredoStyles.ModalHeader>

          <AlfredoStyles.ModalContainer
            style={{ maxHeight: "calc(90vh - calc(var(--u) * 120))" }}
          >
            <AlfredoStyles.ModalBody
              style={{
                maxHeight: isPDV()
                  ? "calc(90vh - 120px)"
                  : "calc(90vh - 120px)",
              }}
            >
              <AlfredoStyles.ItemContainer style={{ height: "100%" }}>
                <AlfredoStyles.ItemTopContainer>
                  <AlfredoStyles.Column style={{ flex: 2 }}>
                    <AlfredoStyles.LabelInputContainer>
                      <AlfredoStyles.Label>Tipo do Item *</AlfredoStyles.Label>
                      <AlfredoStyles.DropdownInput style={{ width: "100%" }}>
                        <select
                          value={localFormData.itemType}
                          onChange={(e) =>
                            handleLocalChange(
                              "itemType",
                              parseInt(e.target.value)
                            )
                          }
                          style={{ width: "100%" }}
                        >
                          <option value="">Selecione o tipo de item...</option>
                          {Object.entries(groupedItemTypes).map(
                            ([category, items]) => (
                              <optgroup
                                key={category}
                                label={ROTULOS_DE_CATEGORIA[category] || category}
                              >
                                {items.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.name} - {type.description}
                                  </option>
                                ))}
                              </optgroup>
                            )
                          )}
                        </select>
                        <label>Categoria</label>
                      </AlfredoStyles.DropdownInput>
                    </AlfredoStyles.LabelInputContainer>

                    <AlfredoStyles.LabelInputContainer>
                      <AlfredoStyles.Label>Nome do Item *</AlfredoStyles.Label>
                      <AlfredoStyles.TextInput style={{ width: "100%" }}>
                        <input
                          type="text"
                          value={localFormData.itemName}
                          onChange={(e) =>
                            handleLocalChange("itemName", e.target.value)
                          }
                          placeholder={
                            currentItemType
                              ? `Ex: ${currentItemType.name} Principal, ${currentItemType.name} 01...`
                              : "Ex: PDV Principal, Mesa 01, Impressora Cozinha..."
                          }
                        />
                        <label>Identificação</label>
                      </AlfredoStyles.TextInput>
                    </AlfredoStyles.LabelInputContainer>

                    <AlfredoStyles.LabelInputContainer>
                      <AlfredoStyles.Label>Descrição</AlfredoStyles.Label>
                      <AlfredoStyles.TextInput style={{ width: "100%" }}>
                        <input
                          type="text"
                          value={localFormData.itemDescription}
                          onChange={(e) =>
                            handleLocalChange("itemDescription", e.target.value)
                          }
                          placeholder={
                            currentItemType
                              ? `Descreva ${currentItemType.name.toLowerCase()} e sua função...`
                              : "Descreva o item e sua função na loja..."
                          }
                        />
                        <label>Descrição</label>
                      </AlfredoStyles.TextInput>
                    </AlfredoStyles.LabelInputContainer>

                  </AlfredoStyles.Column>

                  {selectedObject && (
                    <AlfredoStyles.Column style={{ flex: 1 }}>
                      <AlfredoStyles.InfoGrid>
                        <AlfredoStyles.InfoItem>
                          <AlfredoStyles.InfoLabel>
                            <span className="material-icons">
                              calendar_today
                            </span>
                            Criado em
                          </AlfredoStyles.InfoLabel>
                          <AlfredoStyles.InfoValue>
                            {new Date(
                              selectedObject.createdAt
                            ).toLocaleDateString("pt-BR")}
                          </AlfredoStyles.InfoValue>
                        </AlfredoStyles.InfoItem>

                        <AlfredoStyles.InfoItem>
                          <AlfredoStyles.InfoLabel>
                            <span className="material-icons">update</span>
                            Última atualização
                          </AlfredoStyles.InfoLabel>
                          <AlfredoStyles.InfoValue>
                            {new Date(
                              selectedObject.updatedAt
                            ).toLocaleDateString("pt-BR")}
                          </AlfredoStyles.InfoValue>
                        </AlfredoStyles.InfoItem>

                        <AlfredoStyles.InfoItem>
                          <AlfredoStyles.InfoLabel>
                            <span className="material-icons">person</span>
                            Alterado por
                          </AlfredoStyles.InfoLabel>
                          <AlfredoStyles.InfoValue>
                            {selectedObject.updatedBy || "Sistema"}
                          </AlfredoStyles.InfoValue>
                        </AlfredoStyles.InfoItem>
                      </AlfredoStyles.InfoGrid>
                    </AlfredoStyles.Column>
                  )}
                </AlfredoStyles.ItemTopContainer>

                {renderItemSpecificFields()}
              </AlfredoStyles.ItemContainer>
            </AlfredoStyles.ModalBody>
          </AlfredoStyles.ModalContainer>

          <AlfredoStyles.ModalFooter>
            {isPDV() && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--sp-12)",
                  marginRight: "auto",
                  fontSize: "var(--fs-sm)",
                }}
              >
                <span style={{ color: "#666" }}>Quando aplicar no PDV:</span>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--sp-4)",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="aplicarEm"
                    value="agora"
                    checked={aplicarEm === "agora"}
                    onChange={() => setAplicarEm("agora")}
                  />
                  Agora
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--sp-4)",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="aplicarEm"
                    value="proximoPeriodo"
                    checked={aplicarEm === "proximoPeriodo"}
                    onChange={() => setAplicarEm("proximoPeriodo")}
                  />
                  Na abertura do próximo caixa
                </label>
              </div>
            )}
            <AlfredoStyles.ButtonGroup>
              <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
                <span className="material-icons">close</span>
                Cancelar
              </AlfredoStyles.SecondaryButton>
              <AlfredoStyles.PrimaryButton onClick={handleSubmit}>
                <span className="material-icons">save</span>
                {/* `selectedObject` é `{}` ao criar (useModal faz
                    `object || {}`), e `{}` é truthy: o botão dizia "Atualizar
                    Item" mesmo sem item nenhum aberto. Quem decide é o uuid. */}
                {selectedObject?.itemUuid ? "Atualizar Item" : "Cadastrar Item"}
              </AlfredoStyles.PrimaryButton>
            </AlfredoStyles.ButtonGroup>

            {aviso && (
              <AlfredoStyles.AvisoDoSalvamento $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AvisoDoSalvamento>
            )}

            {/* Só aparece quando há linha na fila — igual ao precedente de
                MeiosPagamento.tsx. Com a fila limpa, o rodapé fica idêntico ao
                de antes, que é o que mantém o contrato visual de 1440. */}
            {temFila && (
              <AlfredoStyles.FilaCaixa>
                <AlfredoStyles.FilaTitulo>
                  Situação da fila
                </AlfredoStyles.FilaTitulo>
                {fila.pendentesAgora?.length > 0 && (
                  <AlfredoStyles.FilaItem>
                    <span className="material-icons">sync</span>
                    {fila.pendentesAgora.length} a caminho do PDV agora
                  </AlfredoStyles.FilaItem>
                )}
                {fila.pendentesProximoPeriodo?.length > 0 && (
                  <AlfredoStyles.FilaItem>
                    <span className="material-icons">schedule</span>
                    {fila.pendentesProximoPeriodo.length} esperando a abertura do
                    próximo caixa
                  </AlfredoStyles.FilaItem>
                )}
                {fila.conflitos?.map((conflito: any) => (
                  <AlfredoStyles.FilaItem key={conflito.eventoUuid}>
                    <span className="material-icons">error_outline</span>
                    Recusado ({conflito.itemInfoNome}): {conflito.erro}
                  </AlfredoStyles.FilaItem>
                ))}
              </AlfredoStyles.FilaCaixa>
            )}
          </AlfredoStyles.ModalFooter>
        </AlfredoStyles.ModalContent>
      </AlfredoStyles.ModalOverlay>

      <EditorDoComponente
        editingComponent={editingComponent}
        setEditingComponent={setEditingComponent}
        updateComponentInSaleOption={updateComponentInSaleOption}
        handleComponentPropChange={handleComponentPropChange}
        removeComponentFromSaleOption={removeComponentFromSaleOption}
      />
    </>,
    document.body
  );
};

export default ItemModal;
